import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AuthContext';

// Nav items. `superadminOnly: true` means the link only renders when admin.role === 'superadmin'.
const NAV = [
  { path: '/admin', label: 'Dashboard', icon: '📊', exact: true },
  { path: '/admin/orders', label: 'Orders', icon: '📦' },
  { path: '/admin/products', label: 'Products', icon: '🍯' },
  { path: '/admin/categories', label: 'Categories', icon: '📂' },
  { path: '/admin/users', label: 'Customers', icon: '👥' },
  { path: '/admin/coupons', label: 'Coupons', icon: '🎟️' },
  { path: '/admin/reviews', label: 'Reviews', icon: '⭐' },
  { path: '/admin/accounts', label: 'Admin Accounts', icon: '🔑', superadminOnly: true },
  { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
];

const AdminLayout = ({ children }) => {
  const { admin, adminLogout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar__logo">
          <img src="/logo.jpg" alt="Qaya KW" height="36" />
          <span>Admin</span>
        </div>

        <nav className="admin-nav">
          {NAV.filter(item => !item.superadminOnly || admin?.role === 'superadmin').map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `admin-nav__item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span>{item.icon}</span> {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <div className="admin-user">
            <strong>{admin?.full_name}</strong>
            <small>{admin?.role}</small>
          </div>
          <button onClick={handleLogout} className="admin-logout">Logout</button>
        </div>
      </aside>

      {sidebarOpen && <div className="admin-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="admin-main">
        <header className="admin-header">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <div className="admin-header__breadcrumb" />
          <div className="admin-header__right">
            <Link to="/" target="_blank" className="admin-header__shop-link">🛍 View Shop</Link>
          </div>
        </header>
        <div className="admin-content">
          {children}
        </div>
      </div>

      <style>{`
        .admin-layout { display: flex; min-height: 100vh; background: #f0f2f5; }
        .admin-sidebar {
          width: 240px; background: var(--olive-dark); color: white; display: flex;
          flex-direction: column; position: fixed; top: 0; left: 0; height: 100vh;
          z-index: 1000; transition: transform 0.3s;
        }
        .admin-sidebar__logo {
          display: flex; align-items: center; gap: 10px; padding: 20px 20px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        .admin-sidebar__logo span { color: var(--gold-light); font-weight: 700; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; }
        .admin-nav { flex: 1; padding: 16px 12px; display: flex; flex-direction: column; gap: 4px; overflow-y: auto; }
        .admin-nav__item {
          display: flex; align-items: center; gap: 12px; padding: 10px 14px; border-radius: 8px;
          font-size: 0.88rem; color: rgba(255,255,255,0.75); transition: all 0.2s;
        }
        .admin-nav__item:hover, .admin-nav__item.active {
          background: rgba(212,148,58,0.2); color: var(--gold-light);
        }
        .admin-sidebar__footer { padding: 16px; border-top: 1px solid rgba(255,255,255,0.1); }
        .admin-user { margin-bottom: 10px; }
        .admin-user strong { display: block; font-size: 0.88rem; }
        .admin-user small { color: rgba(255,255,255,0.5); font-size: 0.75rem; text-transform: capitalize; }
        .admin-logout { color: rgba(255,255,255,0.5); font-size: 0.82rem; text-decoration: underline; font-family: var(--font-sans); }
        .admin-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 999; }
        .admin-main { margin-left: 240px; flex: 1; display: flex; flex-direction: column; min-height: 100vh; }
        .admin-header {
          background: white; padding: 14px 24px; display: flex; align-items: center;
          justify-content: space-between; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          position: sticky; top: 0; z-index: 100;
        }
        .admin-hamburger { display: none; font-size: 1.4rem; }
        .admin-header__shop-link { font-size: 0.85rem; color: var(--gold); font-weight: 600; }
        .admin-content { padding: 28px; flex: 1; }
        @media (max-width: 768px) {
          .admin-sidebar { transform: translateX(-100%); }
          .admin-sidebar.open { transform: translateX(0); }
          .admin-main { margin-left: 0; }
          .admin-hamburger { display: block; }
          .admin-content { padding: 16px; }
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;
